import React, { useState, useCallback } from "react"
import { gql, useMutation, useQuery } from "@apollo/client"

import { Formik, Form } from "formik"
import { CardHeading, Fieldset } from "components"
import { Modal } from "components"

import formatDate from "utils/formatDate"

const SettingsContainer = ({ data, loading, onSubmit }) => {
  const [isEditing, setIsEditing] = useState(false)

  const openModal = useCallback(async () => {
    setIsEditing(true)
  }, [setIsEditing])

  const closeModal = useCallback(async () => {
    setIsEditing(false)
  }, [setIsEditing])

  return (
    <>
      {!data ? (
        <>
          <CardHeading title="Settings" />
          <SettingsEmpty isLoading={loading} onClick={openModal} />
        </>
      ) : (
        <>
          <CardHeading title="Settings" onClick={openModal} />
          <SettingsList data={data} />
        </>
      )}

      <Modal title="Settings" isOpen={isEditing} onClose={closeModal}>
        <SettingsForm data={data} onClose={closeModal} onSubmit={onSubmit} />
      </Modal>
    </>
  )
}

export default SettingsContainer

const SettingsEmpty = ({ isLoading, onClick }) => {
  return (
    <div className="py-16  flex flex-col justify-center items-center">
      {isLoading ? (
        <div>...Loading...</div>
      ) : (
        <>
          <p className="text-center">No Settings</p>

          <button
            onClick={onClick}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="-ml-1 mr-2 h-5 w-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 6v6m0 0v6m0-6h6m-6 0H6"
              />
            </svg>
            Add Settings
          </button>
        </>
      )}
    </div>
  )
}

const SettingsList = ({ data }) => {
  return (
    <dl>
      <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
        <dt className="text-sm leading-5 font-medium text-gray-500">
          Credit Card payment
        </dt>
        <dd className="mt-1 text-sm leading-5 text-gray-900 sm:mt-0 sm:col-span-2">
          {data?.creditCardPayment ? "Yes" : "No"}
        </dd>
      </div>

      <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
        <dt className="text-sm leading-5 font-medium text-gray-500">
          Credit Card Fee
        </dt>
        <dd className="mt-1 text-sm leading-5 text-gray-900 sm:mt-0 sm:col-span-2">
          {`${data?.creditCardFee}%`}
        </dd>
      </div>

      <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
        <dt className="text-sm leading-5 font-medium text-gray-500">
          Check Payment
        </dt>
        <dd className="mt-1 text-sm leading-5 text-gray-900 sm:mt-0 sm:col-span-2">
          {data?.checkPayment ? "Yes" : "No"}
        </dd>
      </div>

      {/* <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
        <dt className="text-sm leading-5 font-medium text-gray-500">
          Check Discount
        </dt>
        <dd className="mt-1 text-sm leading-5 text-gray-900 sm:mt-0 sm:col-span-2">
          {`${data?.checkPaymentDiscount}%`}
        </dd>
      </div> */}

      <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
        <dt className="text-sm leading-5 font-medium text-gray-500">
          Last date to book
        </dt>
        <dd className="mt-1 text-sm leading-5 text-gray-900 sm:mt-0 sm:col-span-2">
          {formatDate(data?.lockTripDate, "MMMM d, yyyy")}
        </dd>
      </div>

      <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
        <dt className="text-sm leading-5 font-medium text-gray-500">
          Requred Traveler Information
        </dt>
        <dd className="mt-1 text-sm leading-5 text-gray-900 sm:mt-0 sm:col-span-2">
          <ul className="list-none">
            {data?.passengerFields &&
              Object.keys(data?.passengerFields)
                ?.sort()
                ?.map((field, index) => (
                  <li key={index} className="flex items-center space-x-4">
                    {data?.passengerFields[field] ? (
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-4 w-4 text-green-400"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          stroke-width="2"
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                    ) : (
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-4 w-4 text-red-400"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M6 18L18 6M6 6l12 12"
                        />
                      </svg>
                    )}

                    <span className="ml-6 capitalize">
                      {field
                        ?.replace(/([A-Z])/g, " $1")
                        .replace("size", "shoe size")}
                    </span>
                  </li>
                ))}
          </ul>
        </dd>
      </div>

      {/* <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
        <dt className="text-sm leading-5 font-medium text-gray-500">
          Email notifications
        </dt>
        <dd className="mt-1 text-sm leading-5 text-gray-900 sm:mt-0 sm:col-span-2">
          {formatDate((data?.lockTripDate), "MMMM d, yyyy")}
        </dd>
      </div> */}
    </dl>
  )
}

const SettingsForm = ({ data, onClose, onSubmit }) => {
  const settingsModel = {
    checkPayment: true,
    // checkPaymentDiscount: 3.5,
    creditCardPayment: true,
    creditCardFee: 3.5,
    lockTripDate: new Date(),

    notifications: [],
    passengerFields: {
      passportNumber: false,
      passportExparationDate: false,

      emergencyContactPerson: true,
      specialRequest: true,
      roomateRequest: true,

      weight: true,
      height: true,
      size: true,

      smoker: true,
      newsletter: true,
    },

    minPassingers: null,
    multiBooking: true,
    reservationOnly: false,
  }

  const initialValues = { ...settingsModel, ...data }

  const formikProps = {
    initialValues: {
      ...initialValues,
      lockTripDate: formatDate(initialValues.lockTripDate, "yyyy-LL-dd"),
    },
    validateOnBlur: false,
    validateOnChange: false,
    // validationSchema: createTripSchema,
    onSubmit: useCallback(async (values, actions) => {
      try {
        await onSubmit(values)
        await onClose()
      } catch (e) {
        console.error("UPDATE TRIP INFROMATION ERROR", e)
        // const errors = e.graphQLErrors.map(v => v)
        // setFieldError("server", errors)
      }
    }),
  }

  return (
    <Formik {...formikProps}>
      {({ values }) => (
        <Form>
          <div className="flex flex-col justify-between">
            <div className="w-6/12">
              <div className="py-3 mt-4 flex">
                <div className="px-2 ">
                  <Fieldset
                    component="switch"
                    name="creditCardPayment"
                    label="Credit Card payment"
                    // preload={true}
                  />
                </div>
                <div className="px-2 ml-4">
                  <Fieldset
                    name="creditCardFee"
                    label="Credit Card Fee"
                    type="number"
                  />
                </div>
              </div>

              <div className="py-3 mt-4 flex">
                <Fieldset
                  component="switch"
                  name="checkPayment"
                  label="Check payment"
                  // preload={true}
                />
              </div>

              <div className="px-2 py-3 mt-4 flex">
                <div>
                  <Fieldset
                    type="date"
                    name="lockTripDate"
                    label="Last date to book"
                    // preload={true}
                  />
                </div>
              </div>
            </div>

            <div className="w-6/12">
              <div className="px-2 py-3 mt-4 flex flex-col">
                <label className="mb-4">Requred Traveler Information</label>

                {[
                  { label: "Passport Number", value: "passportNumber" },
                  {
                    label: "Passport Exparation Date",
                    value: "passportExparationDate",
                  },
                  { label: "Weight", value: "weight" },
                  { label: "Height", value: "height" },
                  { label: "Shoe Size", value: "Size" },
                  { label: "Smoker", value: "smoker" },
                  { label: "Roomate Request", value: "roomateRequest" },
                  {
                    label: "Emergency Contact Person",
                    value: "specialRequest",
                  },
                ].map((field, index) => (
                  <div key={index} className="px-2 ">
                    <Fieldset
                      component="switch"
                      name={`passengerFields.${field.value}`}
                      label={field.label}
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>

          <button type="submit" className="btn-submit">
            Submit
          </button>
        </Form>
      )}
    </Formik>
  )
}
